import { useAtom, useAtomValue } from "jotai";
import { loadable } from "jotai/utils";
import { useCallback } from "react";

import { Logger } from "@moment/logging";

import { $loggedInUser } from "../../internal/authenication";
import { $documentPermissions } from "../../internal/document/sharing";
import {
	$localInvites,
	$localUserPermissions,
	$localWorkspacePermission,
	$pendingPermissionsAtom,
} from "../../internal/document/sharing/pending-permissions.atom";
import type {
	Invitation,
	PermRelation,
	PermissionUpdate,
	UserWithRelation,
} from "../../internal/document/sharing/permissions.types";
import type { SearchUserResult } from "../../internal/document/sharing/users.types";
import { useSearchUserQuery } from "./useSearchUsers";

interface UserOptionItem {
	id: string;
	type: "user";
	user: SearchUserResult;
}

interface InviteOptionItem {
	id: string;
	type: "invite";
	value: string;
}

type HandleSelectItem = UserOptionItem | InviteOptionItem;
const logger = new Logger("useShareTabData");

export const useShareTabData = (documentId: string, workspaceName: string | undefined) => {
	const [pendingPermissions, setPendingPermissions] = useAtom($pendingPermissionsAtom);
	const [localUserPermissions, setLocalUserPermissions] = useAtom($localUserPermissions);
	const [localInvites, setLocalInvites] = useAtom($localInvites);
	const loggedInUser = useAtomValue($loggedInUser);
	const [localWorkspacePermission, setLocalWorkspacePermission] =
		useAtom($localWorkspacePermission);
	const { setQuery } = useSearchUserQuery();

	const [loadablePermissions] = useAtom(
		loadable($documentPermissions({ documentId, workspaceName }))
	);

	// Reset all ShareTab data - useful for cleanup when the tab closes
	const resetShareTabData = useCallback(() => {
		setPendingPermissions(new Map());
		setLocalUserPermissions(new Map());
		setLocalInvites([]);
		setLocalWorkspacePermission(null);
		setQuery("");
	}, [
		setPendingPermissions,
		setLocalUserPermissions,
		setLocalInvites,
		setLocalWorkspacePermission,
		setQuery,
	]);

	if (loadablePermissions.state !== "hasData") {
		return {
			pendingPermissions,
			setPendingPermissions,
			localUserPermissions,
			setLocalUserPermissions,
			localInvites,
			setLocalInvites,
			resetShareTabData,
		};
	}

	const { userPermissions, invites } = loadablePermissions.data;
	let hasChanges = false;
	for (const invite of invites) {
		if (!localInvites.find((i) => i.email === invite.email)) {
			localInvites.push(invite);
			hasChanges = true;
		}
	}
	if (hasChanges) {
		setLocalInvites([...localInvites]);
	}

	hasChanges = false;
	for (const [userId, permission] of userPermissions) {
		if (!localUserPermissions.has(userId)) {
			localUserPermissions.set(userId, permission);
			hasChanges = true;
		}
	}
	if (hasChanges) {
		setLocalUserPermissions(new Map(localUserPermissions));
	}

	let newData: {
		key: string;
		value: PermissionUpdate;
	};
	const handlePermissionChange = (payload: {
		permissionChange: "grant" | "revoke";
		type: "Username" | "Email" | "Workspace";
		relation?: PermRelation;
		user?: SearchUserResult;
		inviteEmail?: string;
		workspaceId?: string;
	}) => {
		if (payload.type === "Workspace") {
			newData = {
				key: payload.workspaceId!,
				value: {
					type: "Workspace",
					action: payload.permissionChange,
					relation: payload.relation,
				},
			};

			setLocalWorkspacePermission(newData.value);
		} else if (payload.type === "Email" && payload.inviteEmail) {
			newData = {
				key: payload.inviteEmail,
				value: {
					type: "Email",
					action: payload.permissionChange,
					relation: payload.relation,
					inviteEmail: payload.inviteEmail,
				},
			};
			setLocalInvites((prev) => {
				for (const invite of prev) {
					if (invite.email === payload.inviteEmail) {
						invite.relation =
							payload.permissionChange === "revoke"
								? "revoke"
								: payload.relation || invite.relation;
						return [...prev];
					}
				}
				return [
					...prev,
					{
						email: payload.inviteEmail!,
						referrer: loggedInUser?.sub || "",
						relation: payload.relation || "collaborator",
					},
				];
			});
		} else if (payload.type === "Username" && payload.user) {
			newData = {
				key: payload.user.user_id,
				value: {
					type: "Username",
					action: payload.permissionChange,
					relation: payload.relation,
					user: payload.user,
				},
			};
			setLocalUserPermissions((prev) => {
				if (newData.value.relation === undefined) {
					logger.error("Relation is undefined for user permission change", {
						payload,
					});
					return prev;
				}
				const users = new Map(prev);

				users.set(newData.key, {
					...payload.user!,
					relation:
						payload.permissionChange === "revoke" ? "revoke" : newData.value.relation,
				});
				return users;
			});
		}

		setPendingPermissions((prev) => {
			const updates = new Map<string, PermissionUpdate>();
			if (newData.value.type === "Workspace") {
				// delete all previous workspace permissions.
				for (const [key, value] of prev) {
					if (value.type === "Workspace") {
						continue;
					}
					updates.set(key, value);
				}
			} else {
				// set all the previous permissions.
				for (const [key, value] of prev) {
					updates.set(key, value);
				}
			}
			updates.set(newData.key, newData.value);
			return updates;
		});
	};
	const clearWorkspacePermission = () => {
		setLocalWorkspacePermission(null);
		setPendingPermissions((prev) => {
			const updates = new Map(prev);
			for (const [key, value] of updates) {
				if (value.type === "Workspace") {
					updates.delete(key);
				}
			}
			return updates;
		});
	};

	// Handler for selecting a user or email to update permission
	const handleUserSelect = (item: HandleSelectItem) => {
		if (!item) {
			return;
		}

		if (item.type === "invite") {
			// Email invite
			const newInvite: Invitation = {
				email: item.value,
				referrer: loggedInUser?.sub || "",
				relation: "collaborator",
			};

			// Add to local invites
			setLocalInvites((prev) => [...prev, newInvite]);

			// Add to pending updates
			setPendingPermissions((prev) => {
				const updates = new Map(prev);
				updates.set(newInvite.email, {
					type: "Email",
					action: "grant",
					relation: "collaborator",
					inviteEmail: newInvite.email,
				});
				return updates;
			});
		} else {
			// User object (SearchUserResult)
			const userWithRelation: UserWithRelation = {
				...item.user,
				relation: "collaborator",
			};

			// Add to local users
			setLocalUserPermissions((prev) => {
				const users = new Map(prev);
				users.set(item.user.user_id, userWithRelation);
				return users;
			});

			// Add to pending updates
			setPendingPermissions((prev) => {
				const updates = new Map(prev);
				updates.set(item.user.user_id, {
					type: "Username",
					action: "grant",
					relation: "collaborator",
					user: item.user,
				});
				return updates;
			});
		}

		// Clear search query
		setQuery("");
	};

	return {
		pendingPermissions,
		localUserPermissions,
		localInvites,
		localWorkspacePermission,
		setLocalWorkspacePermission,
		setLocalInvites,
		handleUserSelect,
		handlePermissionChange,
		clearWorkspacePermission,
		resetShareTabData,
	};
};
